import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface Bowler {
    bowler_id: number;
    bowler_name?: string;
    id: number;
    name?: string;
}

interface DoubleGroup {
    double: number;
    bowlers: Bowler[];
}

const DoublesList: React.FC = () => {
    const { sideEventId } = useParams<{ sideEventId: string }>();
    const [doubles, setDoubles] = useState<DoubleGroup[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

   

    const fetchDoubles = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
            const response = await fetch(`${apiUrl}/admin/manage-side-events/side-events/${sideEventId}/doubles`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (!response.ok) {
                throw new Error('Failed to fetch doubles');
            }
            const data = await response.json();

            // Group bowlers by double number
            const grouped: Record<number, Bowler[]> = {};
            for (const row of Object.values(data.doubles).flat() as any[]) {
                const doubleId = row.double_id ?? 0;
                if (!grouped[doubleId]) grouped[doubleId] = [];
                grouped[doubleId].push(row);
            }
            const doublesArr: DoubleGroup[] = Object.entries(grouped).map(([double, bowlers]) => ({
                double: Number(double),
                bowlers: bowlers as Bowler[]
            }));
            setDoubles(doublesArr);
        } catch (err: any) {
            setError(err.message || 'Error loading doubles');
        } finally {
            setLoading(false);
        }
    }, [sideEventId]);

    useEffect(() => {
        if (sideEventId) fetchDoubles();
    }, [sideEventId, fetchDoubles]);

     const handleRegenerateDoubles = async () => {
        try {
            setLoading(true);
            setError('');
            const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
            const response = await fetch(`${apiUrl}/admin/manage-side-events/side-events/${sideEventId}/generate-doubles`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (!response.ok) {
                throw new Error('Failed to regenerate doubles');
            }
            // Refresh the doubles list after regeneration
            await fetchDoubles();
        } catch (err: any) {
            setError(err.message || 'Error regenerating doubles');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className='min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 p-4'>

        <div className="max-w-2xl mx-auto mt-10 bg-blue-600/50 backdrop-blur-lg rounded-xl p-8 border border-white/20 shadow-lg">
            <h2 className="text-2xl font-bold text-center text-white mb-4">🎳 Generated Doubles For This Side Event</h2>
            {loading && (
                <div className="text-center text-gray-300 py-8">Loading doubles...</div>
            )}
            {error && (
                <div className="bg-red-500/20 border border-red-500 text-red-300 px-4 py-3 rounded-lg text-center mb-4">
                    {error}
                </div>
            )}


            {!loading && !error && (
                <div className="space-y-6">
                    {doubles.length === 0 ? (
                        <div className="text-center text-gray-400">No doubles have been generated yet.</div>
                    ) : (
                        doubles.map((group) => (
                            <div
                                key={group.double}
                                className="flex flex-col md:flex-row items-center justify-between bg-blue-500/50 border border-white/30 rounded-lg px-6 py-4 shadow transition hover:bg-blue-900/30"
                            >
                                <div className="flex-1 flex flex-col md:flex-row md:items-center gap-4">
                                    {group.bowlers.map((bowler, idx) => (
                                        <span
                                            key={bowler.bowler_id}
                                            className="text-lg font-semibold text-white  px-4 py-2"
                                        >
                                            {bowler.bowler_name || `Bowler #${bowler.bowler_id}`}
                                        </span>
                                    ))}
                                    {group.bowlers.length === 1 && (
                                        <span className="text-gray-400 italic ml-2">(No partner)</span>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
            <button className='bg-yellow-600/50 hover:bg-yellow-900/50 text-sm text-white font-semibold px-2 py-2 rounded-lg shadow transition duration-200 mt-4 border border-yellow-400'
            onClick={handleRegenerateDoubles}>
                Regenerate Doubles
            </button>
        </div>
        </div>
    );
};

export default DoublesList;